// =============================================================================
// Pipeline Page (Kanban Board)
// =============================================================================
// Visual pipeline/kanban board for tracking contacts through the sales funnel
// =============================================================================

import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  UserGroupIcon,
  CurrencyDollarIcon,
  ArrowPathIcon,
  UserCircleIcon,
  BuildingOfficeIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import { Card, Button } from '../../../components/ui';
import { fundraisingService } from '../../../services/fundraising.service';
import { getApiError } from '../../../services/api';
import { usePermissions } from '../../../hooks/usePermissions';
import type {
  PipelineColumn,
  PipelineStage,
  ContactWithSubscriptions,
} from '../../../types/fundraising';
import {
  PIPELINE_STAGE_LABELS,
  LEAD_SOURCE_LABELS,
} from '../../../types/fundraising';

// =============================================================================
// Constants
// =============================================================================

const STAGE_COLORS: Record<PipelineStage, { bg: string; border: string; header: string }> = {
  LEAD: { bg: 'bg-gray-50', border: 'border-gray-200', header: 'bg-gray-100' },
  QUALIFIED: { bg: 'bg-blue-50', border: 'border-blue-200', header: 'bg-blue-100' },
  DATA_ROOM: { bg: 'bg-indigo-50', border: 'border-indigo-200', header: 'bg-indigo-100' },
  INTERESTED: { bg: 'bg-purple-50', border: 'border-purple-200', header: 'bg-purple-100' },
  SOFT_COMMIT: { bg: 'bg-yellow-50', border: 'border-yellow-200', header: 'bg-yellow-100' },
  HARD_COMMIT: { bg: 'bg-orange-50', border: 'border-orange-200', header: 'bg-orange-100' },
  DOCS_SENT: { bg: 'bg-cyan-50', border: 'border-cyan-200', header: 'bg-cyan-100' },
  DOCS_SIGNED: { bg: 'bg-teal-50', border: 'border-teal-200', header: 'bg-teal-100' },
  FUNDED: { bg: 'bg-green-50', border: 'border-green-200', header: 'bg-green-100' },
  CONVERTED: { bg: 'bg-emerald-50', border: 'border-emerald-200', header: 'bg-emerald-100' },
  PASSED: { bg: 'bg-red-50', border: 'border-red-200', header: 'bg-red-100' },
  INACTIVE: { bg: 'bg-gray-50', border: 'border-gray-200', header: 'bg-gray-100' },
};

// Display order for pipeline stages (main funnel stages first, then terminal stages)
const DISPLAY_STAGES: PipelineStage[] = [
  'LEAD',
  'QUALIFIED',
  'DATA_ROOM',
  'INTERESTED',
  'SOFT_COMMIT',
  'HARD_COMMIT',
  'DOCS_SENT',
  'DOCS_SIGNED',
  'FUNDED',
  'CONVERTED',
];

const TERMINAL_STAGES: PipelineStage[] = ['PASSED', 'INACTIVE'];

// =============================================================================
// Contact Card Component
// =============================================================================

interface ContactCardProps {
  contact: ContactWithSubscriptions;
  canEdit?: boolean;
}

function ContactCard({ contact, canEdit = true }: ContactCardProps) {
  const [isDragging, setIsDragging] = useState(false);

  const totalCommitment = contact.subscriptions?.reduce((sum, sub) => sum + sub.commitmentAmount, 0) || 0;

  const formatCurrency = (amount: number) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    }
    if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(0)}K`;
    }
    return `$${amount}`;
  };

  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData('contactId', contact.id);
    e.dataTransfer.setData('currentStage', contact.pipelineStage);
    setIsDragging(true);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  return (
    <Link
      to={`/admin/fundraising/contacts/${contact.id}`}
      draggable={canEdit}
      onDragStart={canEdit ? handleDragStart : undefined}
      onDragEnd={canEdit ? handleDragEnd : undefined}
      className={`block bg-white rounded-lg border border-gray-200 p-3 shadow-sm hover:shadow-md transition-shadow ${canEdit ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'} ${
        isDragging ? 'opacity-50' : ''
      }`}
    >
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
          <span className="text-sm font-semibold text-primary-700">
            {contact.firstName.charAt(0)}{contact.lastName.charAt(0)}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-gray-900 truncate">
            {contact.firstName} {contact.lastName}
          </h4>
          {contact.company && (
            <p className="text-xs text-gray-500 truncate flex items-center gap-1">
              <BuildingOfficeIcon className="h-3 w-3" />
              {contact.company}
            </p>
          )}
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-gray-400">
              {LEAD_SOURCE_LABELS[contact.leadSource]}
            </span>
            {totalCommitment > 0 && (
              <span className="text-xs font-medium text-green-600">
                {formatCurrency(totalCommitment)}
              </span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}

// =============================================================================
// Pipeline Column Component
// =============================================================================

interface PipelineColumnProps {
  stage: PipelineStage;
  contacts: ContactWithSubscriptions[];
  onStageChange: (contactId: string, newStage: PipelineStage) => Promise<void>;
  canEdit?: boolean;
}

function PipelineColumnComponent({ stage, contacts, onStageChange, canEdit = true }: PipelineColumnProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const colors = STAGE_COLORS[stage];

  const totalCommitment = contacts.reduce((sum, contact) => {
    return sum + (contact.subscriptions?.reduce((subSum, sub) => subSum + sub.commitmentAmount, 0) || 0);
  }, 0);

  const formatCurrency = (amount: number) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    }
    if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(0)}K`;
    }
    return amount > 0 ? `$${amount}` : '';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (!canEdit) return;
    const contactId = e.dataTransfer.getData('contactId');
    const currentStage = e.dataTransfer.getData('currentStage') as PipelineStage;
    if (contactId && currentStage !== stage) {
      await onStageChange(contactId, stage);
    }
  };

  return (
    <div
      className={`flex flex-col rounded-lg border ${colors.border} ${colors.bg} min-w-[280px] max-w-[280px] h-full ${
        isDragOver ? 'ring-2 ring-primary-500' : ''
      }`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Column Header */}
      <div className={`${colors.header} px-3 py-2 rounded-t-lg border-b ${colors.border}`}>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">{PIPELINE_STAGE_LABELS[stage]}</h3>
          <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-white text-xs font-medium text-gray-700">
            {contacts.length}
          </span>
        </div>
        {totalCommitment > 0 && (
          <p className="text-xs text-gray-600 mt-1">
            {formatCurrency(totalCommitment)} committed
          </p>
        )}
      </div>

      {/* Contact Cards */}
      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {contacts.map((contact) => (
          <ContactCard
            key={contact.id}
            contact={contact}
            canEdit={canEdit}
          />
        ))}
        {contacts.length === 0 && (
          <div className="text-center py-8 text-xs text-gray-400">
            No contacts
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function PipelinePage() {
  const { hasPermission, isLoaded: permsLoaded } = usePermissions();
  const canEdit = hasPermission('fundraising', 'edit');
  const [isLoading, setIsLoading] = useState(true);
  const [columns, setColumns] = useState<PipelineColumn[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showTerminalStages, setShowTerminalStages] = useState(false);

  const loadPipeline = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await fundraisingService.getPipelineView();
      setColumns(data);
    } catch (err) {
      console.error('Failed to load pipeline:', err);
      setError(getApiError(err).message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPipeline();
  }, [loadPipeline]);

  const handleStageChange = async (contactId: string, newStage: PipelineStage) => {
    try {
      await fundraisingService.changePipelineStage(contactId, newStage);
      // Reload the pipeline to get updated data
      await loadPipeline();
    } catch (err) {
      console.error('Failed to change stage:', err);
      setError(getApiError(err).message);
    }
  };

  // Organize columns by stage
  const columnsByStage = columns.reduce((acc, col) => {
    acc[col.stage] = col.contacts;
    return acc;
  }, {} as Record<PipelineStage, ContactWithSubscriptions[]>);

  // Calculate totals
  const totalContacts = columns.reduce((sum, col) => sum + col.contacts.length, 0);
  const totalCommitment = columns.reduce((sum, col) => {
    return sum + col.contacts.reduce((contactSum, contact) => {
      return contactSum + (contact.subscriptions?.reduce((subSum, sub) => subSum + sub.commitmentAmount, 0) || 0);
    }, 0);
  }, 0);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Permission guard - early return after all hooks
  if (permsLoaded && !hasPermission('fundraising', 'view')) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <ShieldExclamationIcon className="h-12 w-12 text-gray-400 mb-3" />
        <h3 className="text-lg font-medium text-gray-900">Access Denied</h3>
        <p className="mt-1 text-sm text-gray-500">You do not have permission to view this page.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="flex gap-4 overflow-x-auto">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-96 w-72 bg-gray-200 rounded-lg flex-shrink-0" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Pipeline</h1>
        <Card padding="lg">
          <div className="text-center py-12">
            <p className="text-red-600">{error}</p>
            <Button className="mt-4" onClick={loadPipeline}>
              Try Again
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pipeline</h1>
          <p className="text-sm text-gray-500 mt-1">
            Drag contacts between stages to update their status
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="secondary" onClick={loadPipeline} className="flex items-center gap-2">
            <ArrowPathIcon className="h-4 w-4" />
            Refresh
          </Button>
          <Link to="/admin/fundraising/contacts">
            <Button>View All Contacts</Button>
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary-100 flex items-center justify-center">
              <UserGroupIcon className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Contacts</p>
              <p className="text-xl font-bold text-gray-900">{totalContacts}</p>
            </div>
          </div>
        </Card>
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
              <CurrencyDollarIcon className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Pipeline Value</p>
              <p className="text-xl font-bold text-gray-900">{formatCurrency(totalCommitment)}</p>
            </div>
          </div>
        </Card>
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <UserCircleIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Qualified</p>
              <p className="text-xl font-bold text-gray-900">{columnsByStage['QUALIFIED']?.length || 0}</p>
            </div>
          </div>
        </Card>
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <CurrencyDollarIcon className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Funded</p>
              <p className="text-xl font-bold text-gray-900">{columnsByStage['FUNDED']?.length || 0}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Toggle Terminal Stages */}
      <div className="flex items-center">
        <button
          onClick={() => setShowTerminalStages(!showTerminalStages)}
          className="text-sm text-gray-500 hover:text-gray-700"
        >
          {showTerminalStages ? 'Hide' : 'Show'} Passed/Inactive ({
            (columnsByStage['PASSED']?.length || 0) + (columnsByStage['INACTIVE']?.length || 0)
          })
        </button>
      </div>

      {/* Pipeline Columns */}
      <div className="flex gap-4 overflow-x-auto pb-4" style={{ minHeight: '500px' }}>
        {DISPLAY_STAGES.map((stage) => (
          <PipelineColumnComponent
            key={stage}
            stage={stage}
            contacts={columnsByStage[stage] || []}
            onStageChange={handleStageChange}
            canEdit={canEdit}
          />
        ))}
        {showTerminalStages && TERMINAL_STAGES.map((stage) => (
          <PipelineColumnComponent
            key={stage}
            stage={stage}
            contacts={columnsByStage[stage] || []}
            onStageChange={handleStageChange}
            canEdit={canEdit}
          />
        ))}
      </div>
    </div>
  );
}

export default PipelinePage;
